import { Component, inject } from '@angular/core';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { JobRoleSummaryDynamicOptionFields, JobRoleSummaryFormArray, JobRoleSummaryTableSchema } from '../../model/job-role-summary.model';
import { DynamicFormArray, FIELD_TYPES, FieldOption, FieldSelectedValue, FormFieldConfig, Props } from '../../model/form.model';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { FieldNames, FieldTypesForOptions } from '../../model/form-fields.model';
import { OptionsFetchConfig } from '../../model/common.model';
import { BaseComponent } from '../base/base.component';
import { CommonUtils } from '../../utils/common.utils';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'app-job-role-summary',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './job-role-summary.component.html',
})
export class JobRoleSummaryComponent extends BaseComponent {
  protected data = 'JobRoleSummaryComponent';
  public dataTableSchema: DataTableSchema = structuredClone(JobRoleSummaryTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(JobRoleSummaryFormArray);
  protected props: Props = {};
  jobRoleSummaryDynamicOptionFields: OptionsFetchConfig = JobRoleSummaryDynamicOptionFields;
  title = '';
  commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.manageDisplayColumns();
  }

  override updateOptions = () => {
    const fieldNames = new Set(this.jobRoleSummaryDynamicOptionFields.fields.map((p) => p.name));
    let apiResult = {} as any;
    const updateFieldWithOptions = () => {
      if (this.filterSchema.FieldsArray.length > 0) {
        this.filterSchema.FieldsArray.forEach((field) => {
          if (FieldTypesForOptions.includes(field['fieldType']) && fieldNames.has(field['fieldName'])) {
            field['isLoading'] = false;
            field['options'] = apiResult[field['fieldName']] ?? [];
          }
        });
      }
    }
    this.service.getDataFromDataSource(this.jobRoleSummaryDynamicOptionFields.searchConfig, ApiEnvNames['JobEnrollment']).then((response) => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result) && response.result.length > 0) {
          apiResult = this.fillValuesFromApi(response.result[0]);
          updateFieldWithOptions()
        } else {
          updateFieldWithOptions()
        }
      } else {
        updateFieldWithOptions()
        console.info(`${ERROR_RESPONSE['layoutErrorPrefix']}`, response['errorMsg']);
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
    }).catch((err) => {
      updateFieldWithOptions();
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    });
  };

  fillValuesFromApi = (response: any) => {
    const result: Record<string, Array<FieldOption>> = {};
    const dropdownData = response;
    if (dropdownData) {
      Object.keys(dropdownData).forEach((fieldName) => {
        if (!result[fieldName]) {
          result[fieldName] = [];
        }
        if (dropdownData[fieldName] && dropdownData[fieldName].length > 0) {
          result[fieldName] = dropdownData[fieldName].map((item: string) => ({
            fieldLabel: item,
            fieldValue: item,
          }));
        } else {
          result[fieldName] = [];
        }

      });
    }
    return result;
  };

  override onSearchClicked = (searchParam: SearchConfig) => {
    searchParam['body'] ??= {};
    this.dataTableSchema.dataSource = [];
    this.setTableLoadingState(true);
    this.resetSorting();
    this.filterSchema['FieldsArray'].forEach((field) => {
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        searchParam['body'][field['fieldName']] = field['selectedValue'];
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchParam['body'][field['fieldName']] = 0;
      }
    });
    this.filterSchema['payload'] = searchParam['body']
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['JobEnrollment']).then((response) => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          this.dataTableSchema.dataSource = response.result;
        }
      } else {
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
      this.setTableLoadingState(false);
    })
      .catch((err) => {
        console.info(err);
        this.setTableLoadingState(false);
      });
  };

  override onResetClicked = () => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field: FormFieldConfig) => {
      if (field.fieldName === FieldNames.CertifiableRoles || field.fieldName === FieldNames.NonCertifiableRoles) {
        field.disabled = false;
      }
      if (field.fieldName === FieldNames.Certification) {
        field.disabled = true;
      }
      return { ...field, selectedValue: field['defaultValue'] };
    }
    );
    this.resetSorting()
    this.manageDisplayColumns();
    this.dataTableSchema = {
      ...this.dataTableSchema,
      inputFilter: '',
      dataSource: []
    };    
    this.checkForAnyEmptyFields();
  };

  manageDisplayColumns() {
    const displayColumns: string[] = [];
    const exportSchema = this.dataTableSchema.exportConfig?.schema;
    this.dataTableSchema.columnSchema.forEach((column) => {
      if (column.fieldKey === FieldNames.District) {
        if (exportSchema?.hasOwnProperty('district')) {
          delete exportSchema.district;
        }
        return;
      }
      displayColumns.push(column.fieldKey);
    });

    this.dataTableSchema.displayedColumns = displayColumns;
  }

  override onControlValueChanged = (event: FieldSelectedValue) => { 
    const field = this.filterSchema.FieldsArray.find((p) => p.fieldName === event.FieldName);
    if (field) {
      field.selectedValue = event.Value;
    }
    if (event.FieldName === FieldNames.Area) {
      this.handleAreaFieldChange(event);
    }
    if (event.FieldName === FieldNames['CertifiableRoles'] || event.FieldName === FieldNames['NonCertifiableRoles']) {
      const certificateFields = {} as Record<string, FormFieldConfig>;
      this.filterSchema.FieldsArray.forEach(field => {
        if ([FieldNames['CertifiableRoles'], FieldNames['NonCertifiableRoles'], FieldNames['Certification']].includes(field['fieldName'] as any)) {
          certificateFields[field['fieldName']] = field
        }
      });
      const selectedField = certificateFields[event.FieldName];
      const dependentField = certificateFields[selectedField['isDependentOn']];
      selectedField['disabled'] = dependentField['disabled'] = false;
      if (Array.isArray(event.Value) && event.Value.length > 0) {
        dependentField['selectedValue'] = '';
        dependentField['disabled'] = true;
        if (event['FieldName'] === FieldNames['CertifiableRoles']) {
          certificateFields[FieldNames['Certification']]['disabled'] = false;
        } else {
          certificateFields[FieldNames['Certification']]['disabled'] = true;
          certificateFields[FieldNames['Certification']]['selectedValue'] = ''
        }
      } else {
        const certificationField = certificateFields[FieldNames['Certification']];
        certificationField['disabled'] = true;
        certificationField['selectedValue'] = '';
      }
    }
    this.checkForAnyEmptyFields();
  };


  handleAreaFieldChange(event: FieldSelectedValue): void {
    const displayColumns: string[] = [];
    const areaOptions = event.FieldArray?.find((item) => item['fieldName'] === event['FieldName']);
    const exportSchema = this.dataTableSchema.exportConfig?.schema;
    const selectedAreaCount = areaOptions?.selectedValue?.length ?? 0;
    const totalOptions = areaOptions?.options?.length ?? 0;
    const shouldHideDistrict = selectedAreaCount === totalOptions;
    this.dataTableSchema.columnSchema.forEach((column) => {
      if (column.fieldKey === FieldNames.District && shouldHideDistrict) {
        if (exportSchema?.hasOwnProperty('district')) {
          delete exportSchema.district;
        }
        return;
      }
      displayColumns.push(column.fieldKey);
      if (column.fieldKey === FieldNames.District && !shouldHideDistrict && exportSchema) {
        exportSchema['district'] = 'DISTRICT';
      }
    });
    this.dataTableSchema.displayedColumns = displayColumns;
    if (selectedAreaCount === 0) {
      this.disableEnableButtons(true);
    }

  }
}
